/**
 * Nutrition playground — the conversational half of the Playground's Nutrition tab.
 *
 * The profile builder to the left of this panel is the SAME one the Exercise tab
 * uses. This side adds the inputs nutrition reads that a workout profile has no
 * place for (declared goal, training phase, readiness, diet, allergies) and then
 * lets you talk to the coach as that invented member.
 *
 * WHY EVERY TURN SHOWS TWO ANSWERS. A member never reads the nutrition
 * specialist directly — the coach relays it in the coach's own voice, and either
 * half can be wrong on its own. The specialist can compute a bad number; the
 * coach can drop an assumption, soften a gate, or invent a figure that was never
 * in the envelope. Showing only the coach hides arithmetic errors behind good
 * writing; showing only the specialist tests something no member will ever see.
 * So each turn renders the coach's prose with the specialist's raw envelope
 * underneath it, and the numbers in the envelope are the ones to check the prose
 * against.
 *
 * Nothing here touches an account. The member is invented from the form, and
 * saves the agent attempts are recorded and discarded — they show up as "would
 * have saved" rather than changing a row.
 */
import { useEffect, useRef, useState } from "react"
import {
  AlertCircle,
  Apple,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  Save,
  Send,
  Trash2,
} from "lucide-react"
import {
  runNutritionPlaygroundChat,
  type NutritionChatMessage,
  type NutritionChatTurn,
  type NutritionEnvelope,
  type NutritionOverrides,
  type PlaygroundProfile,
} from "@/lib/api"
import { cn } from "@/lib/utils"

// Matches NutritionGoal server-side. "Not declared" is a first-class option,
// not a blank: an undeclared goal is a real member state that should make the
// engine run maintenance and say so out loud.
const GOAL_OPTIONS = [
  { value: "", label: "Not declared" },
  { value: "fat_loss", label: "Fat loss" },
  { value: "muscle_gain", label: "Muscle gain" },
  { value: "performance", label: "Performance" },
  { value: "general_health", label: "General health" },
] as const

const READINESS_OPTIONS = [
  { value: "", label: "Unknown" },
  { value: "GREEN", label: "GREEN" },
  { value: "YELLOW", label: "YELLOW" },
  { value: "RED", label: "RED" },
] as const

// Free text server-side; these are the values the food library actually keys on.
const DIET_OPTIONS = [
  "omnivore",
  "vegetarian",
  "vegan",
  "pescatarian",
  "gluten_free",
  "dairy_free",
] as const

/** Scenarios worth a click — the paths that are easy to forget to test. */
const PRESETS: Array<{
  label: string
  hint: string
  overrides: NutritionOverrides
  ask: string
}> = [
  {
    label: "Fat loss, all green",
    hint: "The happy path — every input on file, deficit running.",
    overrides: {
      nutrition_goal: "fat_loss",
      training_phase: 3,
      sessions_per_week: 4,
      is_training_day: true,
      readiness_state: "GREEN",
    },
    ask: "How many calories should I be eating?",
  },
  {
    label: "RED day + deficit",
    hint: "The gate: a declared cut colliding with a bad recovery day.",
    overrides: {
      nutrition_goal: "fat_loss",
      training_phase: 3,
      sessions_per_week: 4,
      is_training_day: false,
      readiness_state: "RED",
      readiness_cause: "poor sleep + elevated resting HR",
    },
    ask: "Should I still be in a deficit today?",
  },
  {
    label: "No goal declared",
    hint: "Full stats, no direction — should run maintenance and SAY so.",
    overrides: {
      nutrition_goal: "",
      training_phase: 2,
      sessions_per_week: 3,
      is_training_day: true,
      readiness_state: "GREEN",
    },
    ask: "What should my daily calories be?",
  },
  {
    label: "Vegan + allergies",
    hint: "Restriction the library must respect, plus an allergy it can't infer.",
    overrides: {
      nutrition_goal: "muscle_gain",
      training_phase: 4,
      sessions_per_week: 5,
      is_training_day: true,
      diet_restriction: "vegan",
      excluded_foods: ["peanuts", "cashews"],
    },
    ask: "Give me some protein sources I should be adding.",
  },
]

const DEFAULT_OVERRIDES: NutritionOverrides = {
  nutrition_goal: "",
  training_phase: null,
  sessions_per_week: null,
  is_training_day: true,
  readiness_state: "GREEN",
  readiness_cause: "",
  diet_restriction: "omnivore",
  current_food_list: [],
  excluded_foods: [],
  profile_missing: false,
}

/** One exchange: what was asked, and everything that came back for it. */
interface Turn {
  question: string
  result?: NutritionChatTurn
  error?: string
}

function splitList(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" title={hint}>
        {label}
      </label>
      {children}
    </div>
  )
}

const inputCls =
  "w-full px-2 py-1.5 border rounded-md bg-background text-xs"

/** Confidence is the single most important word in the envelope: "grounded"
 *  means every number came from the engine with real inputs. Anything else
 *  means the coach must not present the numbers as dialled in. */
function ConfidenceBadge({ value }: { value?: string }) {
  if (!value) return null
  const grounded = value === "grounded"
  return (
    <span
      className={cn(
        "rounded px-1.5 py-0.5 text-[10px] font-medium",
        grounded
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
          : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
      )}
      title={
        grounded
          ? "Every number came from the engine using real inputs."
          : "Not dialled in — guidance only. The coach must not present these as precise."
      }
    >
      {value}
    </span>
  )
}

function StatusBadge({ value }: { value?: string }) {
  if (!value) return null
  const tone =
    value === "ok"
      ? "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300"
      : value === "needs_inputs"
        ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
        : "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", tone)}>
      {value}
    </span>
  )
}

/** The numbers, pulled out of the targets blob so they can be read at a glance
 *  and checked against the coach's prose without expanding raw JSON. */
function TargetsGrid({ targets }: { targets: Record<string, any> }) {
  const macros = (targets.macros || {}) as Record<string, any>
  const hydration = (targets.hydration || {}) as Record<string, any>
  const cells: Array<[string, unknown]> = [
    ["kcal", targets.kcal],
    ["protein", macros.protein_g != null ? `${macros.protein_g} g` : undefined],
    ["carbs", macros.carb_g != null ? `${macros.carb_g} g` : undefined],
    ["fat", macros.fat_g != null ? `${macros.fat_g} g` : undefined],
    ["direction", targets.direction],
    ["TDEE", targets.tdee_kcal],
    ["BMR", targets.bmr_kcal],
    ["activity", targets.activity_band],
    ["protein/lb", targets.protein_g_per_lb],
    ["phase", targets.training_phase],
    ["water", hydration.daily_oz_headline ? `${hydration.daily_oz_headline} oz` : undefined],
  ]
  const shown = cells.filter(([, v]) => v !== undefined && v !== null)
  if (!shown.length) return null
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
      {shown.map(([k, v]) => (
        <div key={k} className="rounded border bg-background px-2 py-1">
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">
            {k}
          </div>
          <div className="text-xs font-semibold tabular-nums">{String(v)}</div>
        </div>
      ))}
    </div>
  )
}

function Envelope({ env }: { env: NutritionEnvelope }) {
  const [rawOpen, setRawOpen] = useState(false)
  const targets = (env.targets || {}) as Record<string, any>
  const gates = env.applied_gates || []
  const assumptions = env.assumptions || []
  const missing = env.missing_inputs || []
  const askFields =
    ((env.ask_bundle as any)?.fields as Array<any> | undefined) || []

  return (
    <div className="rounded-md border bg-muted/30 p-2.5 space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Specialist envelope
        </span>
        <StatusBadge value={env.status} />
        <ConfidenceBadge value={env.confidence} />
        {env.degraded && (
          <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] text-orange-800 dark:bg-orange-950 dark:text-orange-300">
            degraded: {env.degraded}
          </span>
        )}
        {env.duration_ms != null && (
          <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
            {(env.duration_ms / 1000).toFixed(1)}s
          </span>
        )}
      </div>

      {/* Assumptions come BEFORE the numbers — the coach is required to say
          them first, so they're placed where a reader checks that. */}
      {assumptions.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 dark:border-amber-900 dark:bg-amber-950/40">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
            Assumptions — the coach must state these before any number
          </div>
          <ul className="mt-1 list-disc pl-4 text-[11px] text-amber-900 dark:text-amber-200">
            {assumptions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {gates.length > 0 && (
        <div className="rounded border border-red-300 bg-red-50 p-2 dark:border-red-900 dark:bg-red-950/40">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-red-800 dark:text-red-300">
            Gates applied — a paused deficit is never silent
          </div>
          <div className="mt-1 text-[11px] text-red-900 dark:text-red-200">
            {gates.join(", ")}
            {targets.deficit_suspension_reason
              ? ` — ${targets.deficit_suspension_reason}`
              : ""}
          </div>
        </div>
      )}

      {Object.keys(targets).length > 0 && <TargetsGrid targets={targets} />}

      {missing.length > 0 && (
        <div className="text-[11px]">
          <span className="font-medium">Needs first:</span> {missing.join(", ")}
          {askFields.length > 0 && (
            <span className="text-muted-foreground">
              {" "}
              — asked as ONE bundle of {askFields.length}, not one per turn
            </span>
          )}
        </div>
      )}

      {env.recommendations && (
        <details className="text-[11px]">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Recommended foods by pillar
          </summary>
          <div className="mt-1 space-y-1">
            {Object.entries(env.recommendations as Record<string, any>).map(
              ([pillar, foods]) => (
                <div key={pillar}>
                  <span className="font-medium">{pillar}:</span>{" "}
                  {Array.isArray(foods) ? foods.join(", ") : String(foods)}
                </div>
              ),
            )}
          </div>
        </details>
      )}

      {/* The specialist's own words. Worth reading against the coach's above —
          a drift between them is the thing this page exists to catch. */}
      {env.summary && (
        <details className="text-[11px]">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            What the specialist said, before the coach re-voiced it
          </summary>
          <p className="mt-1 whitespace-pre-wrap text-foreground/80">
            {env.summary}
          </p>
        </details>
      )}

      {env.user_message && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-[11px] dark:border-red-900 dark:bg-red-950/40">
          <span className="font-medium">Error path — the only string the coach may relay:</span>{" "}
          {env.user_message}
          {env.error_code && (
            <span className="text-muted-foreground">
              {" "}
              (code: {env.error_code} — never shown to a user)
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
        <span>tools: {(env.tools_called || []).join(", ") || "none"}</span>
        <button
          type="button"
          onClick={() => setRawOpen((v) => !v)}
          className="ml-auto inline-flex items-center gap-0.5 hover:text-foreground"
        >
          {rawOpen ? (
            <ChevronDown className="size-3" />
          ) : (
            <ChevronRight className="size-3" />
          )}
          raw JSON
        </button>
      </div>
      {rawOpen && (
        <pre className="max-h-72 overflow-auto rounded bg-foreground p-2 text-[10px] text-background">
          {JSON.stringify(env, null, 2)}
        </pre>
      )}
    </div>
  )
}

/** Comma-join whatever the DB gave us — the food columns are text[] but a
 *  hand-edited row can hold a plain string. */
function asList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  if (typeof value === "string" && value.trim()) return splitList(value)
  return []
}

export function NutritionCoachPanel({
  profile,
  importedProfile,
}: {
  profile: PlaygroundProfile
  importedProfile?: Record<string, unknown> | null
}) {
  const [ov, setOv] = useState<NutritionOverrides>(DEFAULT_OVERRIDES)
  const [foodsRaw, setFoodsRaw] = useState("")
  const [avoidRaw, setAvoidRaw] = useState("")
  const [draft, setDraft] = useState("")
  const [turns, setTurns] = useState<Turn[]>([])
  const [sending, setSending] = useState(false)
  const [specialistOnly, setSpecialistOnly] = useState(false)
  /** Which real member (if any) the nutrition inputs below were seeded from. */
  const [seededFrom, setSeededFrom] = useState<string | null>(null)
  const endRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [turns, sending])

  // Seed the nutrition-only inputs from an imported member. Keyed on the row's
  // id so this fires ONCE per import rather than on every keystroke in the
  // profile form — manual edits made afterwards must survive.
  const importedId = (importedProfile?.id as string | undefined) ?? null
  useEffect(() => {
    if (!importedProfile || !importedId) {
      setSeededFrom(null)
      return
    }
    const goal = importedProfile.nutrition_goal
    const diet = importedProfile.diet_restriction
    const foods = asList(importedProfile.current_food_list)
    const avoid = asList(importedProfile.excluded_foods)
    setOv((prev) => ({
      ...prev,
      nutrition_goal: typeof goal === "string" ? goal : "",
      diet_restriction: typeof diet === "string" && diet ? diet : "omnivore",
      current_food_list: foods,
      excluded_foods: avoid,
    }))
    setFoodsRaw(foods.join(", "))
    setAvoidRaw(avoid.join(", "))
    const name = [importedProfile.first_name, importedProfile.last_name]
      .filter(Boolean)
      .join(" ")
    setSeededFrom(name || "the imported user")
  }, [importedId]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof NutritionOverrides>(
    key: K,
    value: NutritionOverrides[K],
  ) => setOv((prev) => ({ ...prev, [key]: value }))

  const send = async (text: string, presetOv?: NutritionOverrides) => {
    const question = text.trim()
    if (!question || sending) return
    const effective = presetOv ?? ov
    const payload: NutritionOverrides = {
      ...effective,
      current_food_list: presetOv
        ? effective.current_food_list || []
        : splitList(foodsRaw),
      excluded_foods: presetOv
        ? effective.excluded_foods || []
        : splitList(avoidRaw),
    }

    // The coach carries the conversation, which is what production does — the
    // specialist stays single-turn by design. Only the prose goes back, not the
    // envelopes: the coach's own reply is what it said, and re-feeding raw
    // engine JSON as history would be a conversation no member ever has.
    const history: NutritionChatMessage[] = turns.flatMap((t) =>
      t.result?.coach_reply
        ? [
            { role: "user" as const, content: t.question },
            { role: "assistant" as const, content: t.result.coach_reply },
          ]
        : [],
    )

    setDraft("")
    setTurns((prev) => [...prev, { question }])
    setSending(true)
    try {
      const result = await runNutritionPlaygroundChat({
        profile,
        overrides: payload,
        message: question,
        history,
        specialistOnly,
      })
      setTurns((prev) =>
        prev.map((t, i) => (i === prev.length - 1 ? { ...t, result } : t)),
      )
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed"
      setTurns((prev) =>
        prev.map((t, i) => (i === prev.length - 1 ? { ...t, error: message } : t)),
      )
    } finally {
      setSending(false)
    }
  }

  const applyPreset = (p: (typeof PRESETS)[number]) => {
    const next = { ...DEFAULT_OVERRIDES, ...p.overrides }
    setOv(next)
    setFoodsRaw((next.current_food_list || []).join(", "))
    setAvoidRaw((next.excluded_foods || []).join(", "))
    setDraft(p.ask)
    // A preset overwrites whatever an import seeded, so stop claiming these
    // values came from a real member.
    setSeededFrom(null)
  }

  const missingForTargets = (
    [
      ["weight", profile.weight_lbs],
      ["height", profile.height_feet || profile.height_inches],
      ["age", profile.age],
      ["sex", profile.gender],
    ] as Array<[string, unknown]>
  )
    .filter(([, v]) => v === undefined || v === null || v === "")
    .map(([k]) => k)

  return (
    <div className="space-y-4 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
      <div className="flex items-center gap-2">
        <Apple className="size-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Nutrition coach</h2>
        <span className="text-[10px] text-muted-foreground">
          invented user · no account read or written
        </span>
      </div>

      {/* Nutrition-only inputs. These have no home in a workout profile, but
          the engine reads every one of them from a real member's record. */}
      <div className="rounded-lg border bg-card p-3 space-y-3">
        <div className="text-xs font-semibold">Nutrition inputs</div>

        {seededFrom && (
          <div className="flex items-start gap-1.5 rounded border border-sky-300 bg-sky-50 p-2 text-[11px] text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
            <Info className="mt-0.5 size-3 shrink-0" />
            <span>
              Seeded from {seededFrom}'s real record
              {!ov.nutrition_goal && (
                <>
                  {" "}
                  — they have <strong>no declared nutrition goal</strong>, which
                  is a real state worth testing: the engine should run
                  maintenance and say so
                </>
              )}
              . Phase, sessions and readiness aren't on the profile row, so set
              those yourself.
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Field
            label="Declared goal"
            hint="Undeclared is a real state — the engine should run maintenance and say so."
          >
            <select
              value={ov.nutrition_goal ?? ""}
              onChange={(e) => set("nutrition_goal", e.target.value)}
              className={inputCls}
            >
              {GOAL_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Diet">
            <select
              value={ov.diet_restriction ?? "omnivore"}
              onChange={(e) => set("diet_restriction", e.target.value)}
              className={inputCls}
            >
              {DIET_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Training phase" hint="1–6. Drives the caveat layer.">
            <input
              type="number"
              min={1}
              max={6}
              value={ov.training_phase ?? ""}
              onChange={(e) =>
                set("training_phase", e.target.value ? Number(e.target.value) : null)
              }
              className={inputCls}
            />
          </Field>
          <Field
            label="Sessions / week"
            hint="Plan days, which is what the engine counts — not declared sessions."
          >
            <input
              type="number"
              min={0}
              max={14}
              value={ov.sessions_per_week ?? ""}
              onChange={(e) =>
                set("sessions_per_week", e.target.value ? Number(e.target.value) : null)
              }
              className={inputCls}
            />
          </Field>
          <Field label="Readiness today">
            <select
              value={ov.readiness_state ?? ""}
              onChange={(e) => set("readiness_state", e.target.value)}
              className={inputCls}
            >
              {READINESS_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Readiness cause" hint="Free text, shown to the agent.">
            <input
              value={ov.readiness_cause ?? ""}
              onChange={(e) => set("readiness_cause", e.target.value)}
              placeholder="poor sleep + elevated RHR"
              className={inputCls}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Current foods" hint="Comma-separated. What they already eat.">
            <input
              value={foodsRaw}
              onChange={(e) => setFoodsRaw(e.target.value)}
              placeholder="chicken, rice, broccoli"
              className={inputCls}
            />
          </Field>
          <Field
            label="Avoids / allergies"
            hint="Comma-separated. Things the diet restriction can't express."
          >
            <input
              value={avoidRaw}
              onChange={(e) => setAvoidRaw(e.target.value)}
              placeholder="peanuts, cashews"
              className={inputCls}
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={!!ov.is_training_day}
              onChange={(e) => set("is_training_day", e.target.checked)}
            />
            Training day
          </label>
          <label
            className="flex items-center gap-1.5"
            title="Simulate an unreadable profile row — the agent's fail-closed path."
          >
            <input
              type="checkbox"
              checked={!!ov.profile_missing}
              onChange={(e) => set("profile_missing", e.target.checked)}
            />
            Profile unreadable
          </label>
          <label
            className="flex items-center gap-1.5"
            title="Skip the coach hop and read the specialist alone. Faster and cheaper when you're checking arithmetic, not wording."
          >
            <input
              type="checkbox"
              checked={specialistOnly}
              onChange={(e) => setSpecialistOnly(e.target.checked)}
            />
            Specialist only
          </label>
        </div>

        {missingForTargets.length > 0 && !ov.profile_missing && (
          <div className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <Info className="mt-0.5 size-3 shrink-0" />
            <span>
              No {missingForTargets.join(", ")} in the profile on the left, so a
              calorie target isn't computable — expect the insufficient-inputs
              ask. That's a valid thing to test.
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => applyPreset(p)}
            title={p.hint}
            className="rounded-full border bg-card px-2.5 py-1 text-[11px] hover:bg-accent"
          >
            {p.label}
          </button>
        ))}
        {turns.length > 0 && (
          <button
            type="button"
            onClick={() => setTurns([])}
            className="ml-auto inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <Trash2 className="size-3" /> Clear chat
          </button>
        )}
      </div>

      <div className="space-y-3">
        {turns.length === 0 && !sending && (
          <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
            Build a user on the left, set the nutrition inputs above, then ask
            a question. Every turn shows the coach's reply and the specialist's
            raw numbers underneath, so you can check one against the other.
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className="space-y-2">
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
                {t.question}
              </div>
            </div>

            {t.error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>{t.error}</span>
              </div>
            )}

            {t.result && (
              <div className="space-y-2">
                {t.result.coach_reply && (
                  <div className="rounded-lg border bg-card p-3">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Coach — what the user reads
                    </div>
                    <p className="whitespace-pre-wrap text-sm">
                      {t.result.coach_reply}
                    </p>
                  </div>
                )}

                {!t.result.delegated && !t.result.specialist_only && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                    The coach answered WITHOUT calling the nutrition specialist.
                    If it stated any number about food, that's a hard-rule
                    violation worth capturing.
                  </div>
                )}

                {t.result.specialist && <Envelope env={t.result.specialist} />}

                {t.result.attempted_writes.length > 0 && (
                  <div className="rounded-md border border-sky-300 bg-sky-50 p-2 text-[11px] dark:border-sky-900 dark:bg-sky-950/40">
                    <div className="flex items-center gap-1 font-medium text-sky-900 dark:text-sky-300">
                      <Save className="size-3" /> Would have saved (blocked — no
                      row changed)
                    </div>
                    {t.result.attempted_writes.map((w, j) => (
                      <pre
                        key={j}
                        className="mt-1 overflow-auto whitespace-pre-wrap text-[10px] text-sky-900 dark:text-sky-200"
                      >
                        {w.op} {w.table}: {JSON.stringify(w.payload)}
                      </pre>
                    ))}
                  </div>
                )}

                {t.result.fidelity_notes.length > 0 && (
                  <details className="text-[10px] text-muted-foreground">
                    <summary className="cursor-pointer hover:text-foreground">
                      How this run differs from production (
                      {t.result.fidelity_notes.length}) · doctrine:{" "}
                      {t.result.doctrine_source} · {t.result.model} ·{" "}
                      {(t.result.duration_ms / 1000).toFixed(1)}s
                    </summary>
                    <ul className="mt-1 list-disc pl-4">
                      {t.result.fidelity_notes.map((n, j) => (
                        <li key={j}>{n}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        ))}

        {sending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {specialistOnly
              ? "Asking the specialist..."
              : "Coach is delegating to the nutrition specialist..."}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-4 z-10 flex items-end gap-2 rounded-lg border bg-card p-2 shadow-lg">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              void send(draft)
            }
          }}
          rows={2}
          placeholder="Ask as this user — e.g. is 100g of protein enough? (Enter to send, Shift+Enter for a new line)"
          className="flex-1 resize-none rounded-md border bg-background px-2 py-1.5 text-sm"
        />
        <button
          type="button"
          onClick={() => void send(draft)}
          disabled={sending || !draft.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {sending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Send className="size-4" />
          )}
          Send
        </button>
      </div>
    </div>
  )
}
